import { useCallback, useRef, useState } from "react";
import useApi from "./useApi";
import { saveWorkflowPifApplicant, WorkflowPifApplicantResult } from "../services/workflowService";
import { WorkflowPifApplicantRequestDto } from "../types/workflowDto";

/**
 * - `saving`: the call is in flight; the screen keeps its actions disabled.
 * - `failed`: no answer, or a 409 — Retry resends the SSN still held for it.
 * - `refused`: the server will never accept it (403/404/422); Retry would not help.
 */
export type WorkflowPifApplicantStatus = "idle" | "saving" | "failed" | "refused";

interface PendingApplicant {
  instanceId: number;
  ssn: string;
}

/**
 * Decision P4: a new hire's SSN goes to the PostIF applicant record once pif-1 is submitted. The
 * SSN is held in a ref — never React state, never storage — only until the server has it, so a
 * Retry can resend it after a dropped call. A second call while one is in flight is ignored; the
 * server is keyed by the case anyway, so even a duplicate would update the same record.
 */
const useWorkflowPifApplicant = () => {
  const { executeApi } = useApi();
  const [status, setStatus] = useState<WorkflowPifApplicantStatus>("idle");
  const pendingRef = useRef<PendingApplicant | null>(null);
  const inFlightRef = useRef(false);

  const send = useCallback(
    async (rowVersionTag: string): Promise<boolean> => {
      const pending = pendingRef.current;
      if (!pending || inFlightRef.current) return false;
      inFlightRef.current = true;
      setStatus("saving");
      try {
        const result = await executeApi<WorkflowPifApplicantRequestDto, WorkflowPifApplicantResult>(
          saveWorkflowPifApplicant,
          { instanceId: pending.instanceId, ssn: pending.ssn, rowVersionTag },
        );
        if (result?.kind === "saved") {
          pendingRef.current = null;
          setStatus("idle");
          return true;
        }
        if (!result || result.kind === "conflict") {
          setStatus("failed");
          return false;
        }
        pendingRef.current = null;
        setStatus("refused");
        return false;
      } finally {
        inFlightRef.current = false;
      }
    },
    [executeApi],
  );

  /** Holds the SSN for this case and sends it. */
  const save = useCallback(
    (instanceId: number, ssn: string, rowVersionTag: string) => {
      pendingRef.current = { instanceId, ssn };
      return send(rowVersionTag);
    },
    [send],
  );

  /** Resends the SSN still held, with the case's current tag. */
  const retry = useCallback((rowVersionTag: string) => send(rowVersionTag), [send]);

  /** Drops whatever is held — e.g. Start another. */
  const clear = useCallback(() => {
    pendingRef.current = null;
    setStatus("idle");
  }, []);

  return { status, save, retry, clear };
};

export default useWorkflowPifApplicant;
